import {translate} from "../locale";
import {ZOOM_ONE_PRO_PLAN_CODES} from '../common/constant';
import {getPlansMap, isEmptyVal} from '../utils';
import get from 'lodash-es/get';
import uniq from 'lodash-es/uniq';
import reduce from 'lodash-es/reduce';
import values from 'lodash-es/values';
import flatten from 'lodash-es/flatten';
import includes from 'lodash-es/includes';
import find from 'lodash-es/find';

const usageTypeEnum = {
  personal: 'personal',
  business: 'business',
}

const DEFAULT_ERROR_CODE = '379999';

const NEED_PARSE_ERROR_CODE_MAP = {
  minLicenseLimit: '370305',
  maxLicenseLimit: '370302',
  notMatchMandatoryPlan: '370601',
  withBillingCycle: '370602',
  pendingRenewalApply: '370504',
  pendingDowngrade: '370002',
  with_partner: '370304',
  withUsageType: '370604',
};

const PRECISE_MSG_MAPPING = {
  370001: 'coupon.error.with_pending_downsell',
  // 370002
  [NEED_PARSE_ERROR_CODE_MAP.pendingDowngrade]: 'coupon.error.with_pending_downgrade',
  370101: 'coupon.error.invalid',
  370201: 'coupon.error.inactive',
  370202: 'coupon.error.redeemed',
  370301: 'coupon.error.maximum_times',
  // 370305
  [NEED_PARSE_ERROR_CODE_MAP.minLicenseLimit]: 'coupon.error.minimum_licence',
  // 370302
  [NEED_PARSE_ERROR_CODE_MAP.maxLicenseLimit]: 'coupon.error.maximum_licence',
  370303: 'coupon.error.currency',
  370401: 'coupon.error.with_new_subscription',
  370402: 'coupon.error.with_replace_subscription',
  370501: 'coupon.error.pre_existing',
  370502: 'coupon.error.same_coupon_code',
  370503: 'coupon.error.maximum_coupon_count',
  // user ineligible, pending coupon - coupon will expire before subscription term end
  370505: 'coupon.error.pending_will_expired_by_end_subscription_term',
  370603: 'coupon.error.country',
  // 370304
  [NEED_PARSE_ERROR_CODE_MAP.with_partner]: 'coupon.error.with_partner',
  // 370504
  [NEED_PARSE_ERROR_CODE_MAP.pendingRenewalApply]: 'coupon.error.pending_renewal_apply',
  // 370601
  [NEED_PARSE_ERROR_CODE_MAP.notMatchMandatoryPlan]: 'coupon.error.not_match_mandatory_plan',
  // 370602
  [NEED_PARSE_ERROR_CODE_MAP.withBillingCycle]: 'coupon.error.with_billing_cycle',
  // 379999
  [DEFAULT_ERROR_CODE]: 'coupon.error.ineligible',
};

export class BaseCouponErrorHandler {
  constructor(couponResult = {}, planList = [], supportPlanTypeList = []) {
    this.planListMap = getPlansMap(this.preTransferPlanList(planList)) || {};
    this.couponResult = couponResult;
    this.supportPlanTypeList = supportPlanTypeList;
  }

  preTransferPlanList(planList) {
    return planList;
  }

  // need overwrite by caller (params -> plan)
  getOnlinePlanName() {
    return '';
  }

  // need overwrite by caller (params -> time)
  dateTimeFormat(planExpiredTime) {
    return planExpiredTime;
  }

  getDefaultErrorMessage() {
    return translate(PRECISE_MSG_MAPPING[DEFAULT_ERROR_CODE]);
  }

  getPlanNameList(planCodeList = [], isNeedBillingCycle = true) {
    const planTypeMap = reduce(planCodeList, (pre, planCode) => {
      const matchProPlanCodeList = find(ZOOM_ONE_PRO_PLAN_CODES, (planCodeList) => includes(planCodeList, planCode));
      planCode = matchProPlanCodeList
        ? find(matchProPlanCodeList, (p) => {
          return this.planListMap?.[p];
        })
        : planCode;
      const plan = get(this.planListMap, planCode, {});
      const planType = get(plan, 'type', '');

      // need filter not support plan
      if (!isEmptyVal(this.supportPlanTypeList) && !includes(this.supportPlanTypeList, planType)) {
        return pre;
      }

      const planList = get(pre, [planType], []);

      return {
        ...pre,
        [planType]: [...planList, plan],
      };
    }, {});

    const planValueList = flatten(values(planTypeMap));

    const planNameList = reduce(planValueList, (pre, plan) => {
      return [...pre, this.getOnlinePlanName(plan, {isNeedBillingCycle})];
    }, []).filter(Boolean);

    return uniq(planNameList);
  }

  formatMandatoryPlan() {
    // [plan1, plan2, plan3]
    const {planCodeList = []} = this.couponResult;
    const i18nKey = PRECISE_MSG_MAPPING[NEED_PARSE_ERROR_CODE_MAP.notMatchMandatoryPlan];
    const planName = this.getPlanNameList(planCodeList, false)?.join('/');

    return planName ? translate(i18nKey, [planName]) : this.getDefaultErrorMessage();
  }

  formatWithBillingCycle() {
    // [plan1, plan2, plan3]
    const {planCodeList = []} = this.couponResult;
    const i18nKey = PRECISE_MSG_MAPPING[NEED_PARSE_ERROR_CODE_MAP.withBillingCycle];
    const planName = this.getPlanNameList(planCodeList)?.join(', ');

    return planName ? translate(i18nKey, [planName]) : this.getDefaultErrorMessage();
  }

  formatWithLicenseMaxLimit() {
    const {license} = this.couponResult;
    const i18nKey = license > 1 ? 'coupon.error.maximum_licences' : 'coupon.error.maximum_licence';
    return license ? translate(i18nKey, [license]) : this.getDefaultErrorMessage();
  }

  formatWithLicenseMinLimit() {
    const {license} = this.couponResult;
    const i18nKey = PRECISE_MSG_MAPPING[NEED_PARSE_ERROR_CODE_MAP.minLicenseLimit];
    return license ? translate(i18nKey, [license]) : this.getDefaultErrorMessage();
  }

  formatWithPendingRenewal() {
    return translate(PRECISE_MSG_MAPPING[NEED_PARSE_ERROR_CODE_MAP.pendingRenewalApply]);
  }

  formatWithPartner() {
    const {partnerName = ''} = this.couponResult;
    return translate(PRECISE_MSG_MAPPING[NEED_PARSE_ERROR_CODE_MAP.with_partner], [partnerName]);
  }

  formatWithUsageType() {
    const {customerType = ''} = this.couponResult;
    if (customerType === usageTypeEnum.business) {
      return translate('coupon.error.usage_type_for_business');
    }

    if (customerType === usageTypeEnum.personal) {
      return translate('coupon.error.usage_type_for_personal');
    }

    return this.getDefaultErrorMessage();
  }

  formatWithPendingDowngrade() {
    const {planExpiredTime} = this.couponResult;

    if (isEmptyVal(planExpiredTime)) {
      return this.getDefaultErrorMessage();
    }

    return translate(PRECISE_MSG_MAPPING[NEED_PARSE_ERROR_CODE_MAP.pendingDowngrade], [
      this.dateTimeFormat(planExpiredTime),
    ]);
  }

  getErrorMsgWithErrorCode() {
    const {errorCode = ''} = this.couponResult;

    switch (errorCode) {
      case NEED_PARSE_ERROR_CODE_MAP.withBillingCycle:
        return this.formatWithBillingCycle();
      case NEED_PARSE_ERROR_CODE_MAP.notMatchMandatoryPlan:
        return this.formatMandatoryPlan();
      case NEED_PARSE_ERROR_CODE_MAP.minLicenseLimit:
        return this.formatWithLicenseMinLimit();
      case NEED_PARSE_ERROR_CODE_MAP.maxLicenseLimit:
        return this.formatWithLicenseMaxLimit();
      case NEED_PARSE_ERROR_CODE_MAP.pendingRenewalApply:
        return this.formatWithPendingRenewal();
      case NEED_PARSE_ERROR_CODE_MAP.pendingDowngrade:
        return this.formatWithPendingDowngrade();
      case NEED_PARSE_ERROR_CODE_MAP.with_partner:
        return this.formatWithPartner();
      case NEED_PARSE_ERROR_CODE_MAP.withUsageType:
        return this.formatWithUsageType();
      default:
        if (PRECISE_MSG_MAPPING[errorCode]) {
          return translate(PRECISE_MSG_MAPPING[errorCode]);
        }

        return this.getDefaultErrorMessage();
    }
  }
}
